import React, {useCallback} from 'react';
import {Tooltip} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import {useHistory} from 'react-router-dom';
import IconButton from '../base/button/icon_button';
import type {PageBackButtonProps} from './types';

/**
 * Back control: IconButton to the left of the page title.
 * Navigation target must be explicit (`to` / `href` / `onClick` / `historyBack`).
 */
const PageBackButton = ({
    to,
    href,
    onClick,
    historyBack = false,
    label,
    icon = <ArrowBack/>,
}: PageBackButtonProps): React.ReactElement => {
    const history = useHistory();

    /**
     * Resolves navigation from the declared target props.
     */
    const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        if (onClick) {
            onClick(e);
            return;
        }
        if (historyBack) {
            history.goBack();
            return;
        }
        if (to) {
            history.push(to);
            return;
        }
        if (href) {
            window.location.assign(href);
        }
    }, [history, historyBack, href, onClick, to]);

    return (
        <Tooltip title={label}>
            <IconButton
                color="default"
                size="small"
                onPressed={handleClick}
                aria-label={label}
                sx={{mr: 0.5, flexShrink: 0}}
            >
                {icon}
            </IconButton>
        </Tooltip>
    );
};

export default PageBackButton;
